// Hybrid approach: Web Crypto API with backward compatibility
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || "default_secret_key";

// Simple hash function for keys (deterministic)
function hashKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    const char = key.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `key_${Math.abs(hash)}`;
}

// Check if Web Crypto API is available
const isWebCryptoAvailable = typeof crypto !== 'undefined' && crypto.subtle;

// Web Crypto API functions
async function deriveKey(password: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveBits", "deriveKey"]
  );
  
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode("secure-session-salt"),
      iterations: 100000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

async function encryptWithWebCrypto(text: string): Promise<string> {
  try {
    const key = await deriveKey(SECRET_KEY);
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      data
    );
    
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Web Crypto encryption failed:', error);
    return simpleEncrypt(text); // Fallback to simple encryption
  }
}

async function decryptWithWebCrypto(ciphertext: string): Promise<string | null> {
  try {
    const key = await deriveKey(SECRET_KEY);
    const combined = new Uint8Array(atob(ciphertext).split('').map(c => c.charCodeAt(0)));
    
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encrypted
    );
    
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error('Web Crypto decryption failed:', error);
    return simpleDecrypt(ciphertext); // Fallback to simple decryption
  }
}

// Fallback simple encryption (current implementation)
function simpleEncrypt(text: string): string {
  const key = SECRET_KEY;
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    result += String.fromCharCode(charCode);
  }
  return btoa(result);
}

function simpleDecrypt(encryptedText: string): string | null {
  try {
    const decoded = atob(encryptedText);
    const key = SECRET_KEY;
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
      result += String.fromCharCode(charCode);
    }
    return result;
  } catch {
    return null;
  }
}

// Cache for decrypted values to avoid repeated async calls
const decryptionCache = new Map<string, string | null>();

// Detect if a value is encrypted (base64 format) or plain text
function isEncrypted(value: string): boolean {
  try {
    // Try to decode as base64
    atob(value);
    // If successful, it's likely encrypted
    return true;
  } catch {
    // If base64 decode fails, it's plain text
    return false;
  }
}

// Synchronous wrapper that handles async operations internally
export function getDecryptedItemSync(key: string): string | null {
  const hashedKey = hashKey(key);
  const encryptedValue = sessionStorage.getItem(hashedKey);
  
  if (!encryptedValue) return null;
  
  // Check cache first
  if (decryptionCache.has(hashedKey)) {
    return decryptionCache.get(hashedKey) ?? null;
  }
  
  // If the value is not encrypted (plain text), return it directly
  if (!isEncrypted(encryptedValue)) {
    decryptionCache.set(hashedKey, encryptedValue);
    return encryptedValue;
  }
  
  // Try simple decryption first (faster and more reliable)
  const simpleResult = simpleDecrypt(encryptedValue);
  if (simpleResult !== null) {
    decryptionCache.set(hashedKey, simpleResult);
    return simpleResult;
  }
  
  // If simple decryption fails, it might be Web Crypto encrypted
  // For now, return the encrypted value and let async functions handle it
  return encryptedValue;
}

// Function to wait for pending decryptions (useful for components that need the actual value)
export async function waitForDecryption(key: string): Promise<string | null> {
  const hashedKey = hashKey(key);
  const encryptedValue = sessionStorage.getItem(hashedKey);
  
  if (!encryptedValue) return null;
  
  // Check cache first
  if (decryptionCache.has(hashedKey)) {
    return decryptionCache.get(hashedKey) ?? null;
  }
  
  // If not encrypted, return directly
  if (!isEncrypted(encryptedValue)) {
    decryptionCache.set(hashedKey, encryptedValue);
    return encryptedValue;
  }
  
  // Try simple decryption first
  const simpleResult = simpleDecrypt(encryptedValue);
  if (simpleResult !== null) {
    decryptionCache.set(hashedKey, simpleResult);
    return simpleResult;
  }
  
  // Try Web Crypto decryption
  if (isWebCryptoAvailable) {
    try {
      const result = await decryptWithWebCrypto(encryptedValue);
      decryptionCache.set(hashedKey, result);
      return result;
    } catch {
      // If all decryption fails, return the raw value
      return encryptedValue;
    }
  }
  
  // If all decryption fails, return the raw value
  return encryptedValue;
}

// Function to preload and cache a value
export function preloadDecryptedItem(key: string): void {
  const hashedKey = hashKey(key);
  const encryptedValue = sessionStorage.getItem(hashedKey);
  
  if (!encryptedValue || decryptionCache.has(hashedKey)) return;
  
  // If not encrypted, cache directly
  if (!isEncrypted(encryptedValue)) {
    decryptionCache.set(hashedKey, encryptedValue);
    return;
  }
  
  // Try simple decryption first
  const simpleResult = simpleDecrypt(encryptedValue);
  if (simpleResult !== null) {
    decryptionCache.set(hashedKey, simpleResult);
    return;
  }
  
  // Try Web Crypto decryption
  if (isWebCryptoAvailable) {
    decryptWithWebCrypto(encryptedValue)
      .then(result => {
        decryptionCache.set(hashedKey, result);
      })
      .catch(() => {
        // If all decryption fails, cache the raw value
        decryptionCache.set(hashedKey, encryptedValue);
      });
  } else {
    // If all decryption fails, cache the raw value
    decryptionCache.set(hashedKey, encryptedValue);
  }
}

export function setEncryptedItem(key: string, value: string) {
  const hashedKey = hashKey(key);
  
  // For now, use simple encryption to ensure compatibility
  // This prevents the session reset issue
  const encryptedValue = simpleEncrypt(value);
  sessionStorage.setItem(hashedKey, encryptedValue);
  decryptionCache.delete(hashedKey);
}

export function removeEncryptedItem(key: string) {
  const hashedKey = hashKey(key);
  sessionStorage.removeItem(hashedKey);
  decryptionCache.delete(hashedKey);
}

// Original function for backward compatibility (uses sync wrapper)
export function getDecryptedItem(key: string): string | null {
  return getDecryptedItemSync(key);
}

// Async function for when you need the actual decrypted value
export async function getDecryptedItemAsync(key: string): Promise<string | null> {
  const hashedKey = hashKey(key);
  const encryptedValue = sessionStorage.getItem(hashedKey);
  
  if (!encryptedValue) return null;
  
  // Check cache first
  if (decryptionCache.has(hashedKey)) {
    return decryptionCache.get(hashedKey) ?? null;
  }
  
  // If not encrypted, return directly
  if (!isEncrypted(encryptedValue)) {
    decryptionCache.set(hashedKey, encryptedValue);
    return encryptedValue;
  }
  
  // Try simple decryption first
  const simpleResult = simpleDecrypt(encryptedValue);
  if (simpleResult !== null) {
    decryptionCache.set(hashedKey, simpleResult);
    return simpleResult;
  }
  
  // Try Web Crypto decryption
  if (isWebCryptoAvailable) {
    try {
      const result = await decryptWithWebCrypto(encryptedValue);
      decryptionCache.set(hashedKey, result);
      return result;
    } catch {
      // Fallback to simple decryption
      const result = simpleDecrypt(encryptedValue);
      decryptionCache.set(hashedKey, result);
      return result;
    }
  } else {
    const result = simpleDecrypt(encryptedValue);
    decryptionCache.set(hashedKey, result);
    return result;
  }
}

// Debug utility for troubleshooting session issues
export function debugSecureSession(): void {
  //console.log('=== Secure Session Debug ===');
  
  // Check if Web Crypto is available
  //console.log('Web Crypto Available:', isWebCryptoAvailable);
  
  // List all session storage keys
  const allKeys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key) allKeys.push(key);
  }
  
  //console.log('All Session Storage Keys:', allKeys);
  
  // Check critical keys
  const criticalKeys = ['user_id', 'auth_token', 'shop', 'host', 'role_type'];
  criticalKeys.forEach(key => {
    const hashedKey = hashKey(key);
    const encryptedValue = sessionStorage.getItem(hashedKey);
    const decryptedValue = getDecryptedItemSync(key);
    
    // console.log(`Key: ${key}`, {
    //   hashedKey,
    //   hasEncryptedValue: !!encryptedValue,
    //   encryptedValueLength: encryptedValue?.length || 0,
    //   decryptedValue: decryptedValue ? `${decryptedValue.substring(0, 10)}...` : null,
    //   isEncrypted: encryptedValue ? isEncrypted(encryptedValue) : false,
    //   inCache: decryptionCache.has(hashedKey)
    // });
  });
  
  // Check cache status
  // console.log('Cache Status:', {
  //   cacheSize: decryptionCache.size,
  //   cachedKeys: Array.from(decryptionCache.keys())
  // });
  
  // console.log('=== End Debug ===');
}

// Utility to clear all session data (for testing)
export function clearAllSecureSession(): void {
  //console.log('Clearing all secure session data...');
  
  // Clear all session storage
  sessionStorage.clear();
  
  // Clear cache
  decryptionCache.clear();
  
  //console.log('All secure session data cleared');
} 